// src/stores/v2/task.ts
import { defineStore } from 'pinia'
import { ref, readonly } from 'vue'
import { getTaskService } from '@/services/serviceFactory'
import { TaskSearch } from '@/services/task/TaskSearch'
import { TaskService } from '@/services/task/TaskService'
import { useSession } from '@/stores/authStore'
import { type Task } from '@/types/Task.ts'

export const useTaskStoreV2 = defineStore('task.v2', () => {
  // State
  const loading = ref(false)
  const cache = ref({} as Record<string, Task>)

  // Service instance - we'll get it lazily
  let _service: TaskService | null = null

  async function getService(): Promise<TaskService> {
    if (!_service) {
      _service = await getTaskService()
    }
    return _service
  }

  // Getters
  const getSearch = async () => {
    // Get session from authStore
    const session = useSession()

    // Wait for session to be ready
    let attempts = 0
    while (!session.tenantId && attempts < 50) {
      // Wait up to 5 seconds
      await new Promise((resolve) => setTimeout(resolve, 100))
      attempts++
    }

    if (!session.tenantId) {
      throw new Error('Session not ready: tenantId is required')
    }

    const service = await getService()
    return new TaskSearch(session, service['firestore'])
  }

  // Actions
  async function get(id: string): Promise<Task | null> {
    // Check cache first
    if (cache.value[id]) return cache.value[id]

    // Fetch from Firestore using service
    const service = await getService()
    const task = await service.get(id)
    if (task) cache.value[id] = task
    return task
  }

  async function create(data: Partial<Task>): Promise<string> {
    loading.value = true
    try {
      const service = await getService()
      return await service.create(data)
    } finally {
      loading.value = false
    }
  }

  async function update(id: string, data: Partial<Task>): Promise<void> {
    loading.value = true
    try {
      const service = await getService()
      await service.update(id, data)
      // Clear cache for this item
      delete cache.value[id]
    } finally {
      loading.value = false
    }
  }

  async function remove(id: string): Promise<void> {
    loading.value = true
    try {
      const service = await getService()
      await service.delete(id)
      // Clear cache for this item
      delete cache.value[id]
    } finally {
      loading.value = false
    }
  }

  function clearCache() {
    cache.value = {}
  }

  return {
    // State
    loading: readonly(loading),
    cache: readonly(cache),

    // Getters
    search: getSearch,

    // Actions
    get,
    create,
    update,
    remove,
    clearCache,
  }
})

// Export the old name for backward compatibility
export const useTaskStore = useTaskStoreV2
